/**
 * REVYN — Personalised Kit Selector
 * File: assets/revyn-kit-selector.js
 *
 * ─────────────────────────────────────────────────────────────
 * RECOMMENDATION LOGIC (simplified)
 * ─────────────────────────────────────────────────────────────
 * Only two inputs drive everything, and this is intentionally
 * independent of whatever product page the widget is rendered on:
 *
 *   1. Scalp Type  → which of the 4 kits to recommend
 *        stage1 → essentials | stage2 → advance
 *        stage3 → elite      | stage4 → recoveryMax
 *
 *   2. Pack Size   → quantity of that kit + the discount applied
 *        1 Month → qty 1, 0% off
 *        2 Months → qty 2, 10% off
 *        3 Months → qty 3, 20% off
 *
 * UX RULE: selecting/changing scalp type auto-selects "1 Month"
 * immediately, so the result card and price appear right away
 * without waiting on a second click. The user can still upgrade
 * to 2/3 Months manually afterward at any time.
 *
 * NOTE: the discount % here MUST match the Shopify automatic
 * discount configured on these 4 kit products (10% off at qty 2,
 * 20% off at qty 3), so the price shown here matches what checkout
 * actually charges. If you ever change one, change the other.
 */

(function () {
  'use strict';

  function hideNativeVariantPicker() {
    document.querySelectorAll('[data-pgal-option]').forEach(function (group) {
      var label = group.previousElementSibling;
      if (label && label.classList && label.classList.contains('evoke-pdp__variant-label')) {
        label.style.display = 'none';
      }
      group.style.display = 'none';
    });
  }

  function hideQuantityStepper() {
    document.querySelectorAll('[data-pgal-qty-minus]').forEach(function (btn) {
      var wrap = btn.closest('.evoke-pdp__qty');
      if (wrap) wrap.style.display = 'none';
    });
    document.querySelectorAll('.evoke-pdp__variant-label').forEach(function (el) {
      if (el.textContent.trim() === 'Quantity') el.style.display = 'none';
    });
  }

  var SCALP_TO_KIT = {
    stage1: 'essentials',
    stage2: 'advance',
    stage3: 'elite',
    stage4: 'recoveryMax'
  };

  var PACK_RULES = {
    '1m': { qty: 1, discountPct: 0, label: '1 Month' },
    '2m': { qty: 2, discountPct: 10, label: '2 Months' },
    '3m': { qty: 3, discountPct: 20, label: '3 Months' }
  };

  var DEFAULT_PACK_SIZE = '1m'; // ← auto-selected whenever scalp type changes

  var QUESTION_ORDER = ['scalpType', 'packSize'];

  function initSelector(root) {
    if (root.dataset.revynInit) return;
    root.dataset.revynInit = 'true';

    hideNativeVariantPicker();
    hideQuantityStepper();

    var catalogEl = root.querySelector('[data-revyn-kit-catalog]');
    var catalog = {};
    try { catalog = JSON.parse(catalogEl.textContent); } catch (e) { catalog = {}; }

    var state = { scalpType: null, packSize: null };
    var startedTracked = false;

    var errorEl = root.querySelector('[data-revyn-error]');
    var resultEl = root.querySelector('[data-revyn-result]');

    function track(eventName, detail) {
      try {
        document.dispatchEvent(new CustomEvent('revyn:' + eventName, { detail: detail || {} }));
        if (window.dataLayer) window.dataLayer.push({ event: 'revyn_' + eventName, ...detail });
      } catch (e) { /* analytics must never break the UI */ }
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }
    function clearError() {
      if (!errorEl) return;
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    function money(paise) {
      var n = Number(paise);
      if (isNaN(n)) return '';
      try {
        return (n / 100).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
      } catch (e) {
        return '₹' + (n / 100).toFixed(0);
      }
    }

    function decodeEntities(str) {
      if (!str) return '';
      var el = document.createElement('textarea');
      el.innerHTML = str;
      return el.value;
    }

    function flashHighlight(el) {
      if (!el) return;
      el.classList.remove('is-highlighted');
      void el.offsetWidth;
      el.classList.add('is-highlighted');
    }

    function revealQuestion(index) {
      var q = root.querySelector('[data-revyn-q-index="' + index + '"]');
      if (q) q.hidden = false;
    }

    // ── NEW: programmatically select a pack-size option, mirroring
    // exactly what a manual click on that button would do (active
    // class, state update, tracking), so both paths stay in sync. ──
    function selectPackSizeProgrammatically(value) {
      var qEl = root.querySelector('[data-revyn-q="packSize"]');
      if (!qEl) return;
      var optsWrap = qEl.querySelector('[data-revyn-options]');
      var btn = optsWrap.querySelector('.revyn-ks__opt[data-value="' + value + '"]');
      if (!btn) return;

      optsWrap.querySelectorAll('.revyn-ks__opt').forEach(function (b) { b.classList.remove('is-active'); });
      btn.classList.add('is-active');
      qEl.classList.add('is-answered');

      state.packSize = value;
      track('packSize_selected', { value: value, auto: true });
    }

    function bindQuestion(key) {
      var qEl = root.querySelector('[data-revyn-q="' + key + '"]');
      if (!qEl) return;
      var optsWrap = qEl.querySelector('[data-revyn-options]');
      optsWrap.addEventListener('click', function (e) {
        var btn = e.target.closest('.revyn-ks__opt');
        if (!btn || btn.disabled) return;

        if (!startedTracked) { startedTracked = true; track('kit_selection_started', {}); }

        optsWrap.querySelectorAll('.revyn-ks__opt').forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        qEl.classList.add('is-answered');

        state[key] = btn.dataset.value;
        clearError();
        track(key + '_selected', { value: state[key] });

        var qIndex = QUESTION_ORDER.indexOf(key);
        if (qIndex < QUESTION_ORDER.length - 1) {
          revealQuestion(qIndex + 1);
        }

        // ── NEW: whenever scalp type is picked (or changed), auto-select
        // "1 Month" immediately so the result renders without waiting on
        // a second click. If the user already picked a pack size and is
        // just re-picking scalp type, this resets back to 1 Month per
        // the requested behavior ("whenever scalp type changes").
        if (key === 'scalpType') {
          selectPackSizeProgrammatically(DEFAULT_PACK_SIZE);
        }

        maybeRecommend();
      });
    }

    QUESTION_ORDER.forEach(bindQuestion);

    // ── Recommendation engine ──
    function resolveKitKey() {
      return SCALP_TO_KIT[state.scalpType] || null;
    }

    function resolveBaseVariant(kitKey) {
      var kit = catalog[kitKey];
      if (!kit || !kit.exists || !kit.variants || !kit.variants.length) return { kit: kit, variant: null };
      var variant = kit.variants.find(function (v) { return v.available; }) || kit.variants[0];
      return { kit: kit, variant: variant };
    }

    function computePricing(unitPricePaise, packSize) {
      var rule = PACK_RULES[packSize];
      var unit = Number(unitPricePaise) || 0;
      var lineTotal = unit * rule.qty;
      var discountedTotal = Math.round(lineTotal * (1 - rule.discountPct / 100));
      return {
        qty: rule.qty,
        discountPct: rule.discountPct,
        packLabel: rule.label,
        lineTotal: lineTotal,
        discountedTotal: discountedTotal
      };
    }

    var currentResolution = null;

    function maybeRecommend() {
      if (!state.scalpType || !state.packSize) return;

      var kitKey = resolveKitKey();
      if (!kitKey) {
        showError("We couldn't match a kit to that scalp type. Please contact us for a recommendation.");
        resultEl.hidden = true;
        return;
      }

      var resolved = resolveBaseVariant(kitKey);
      if (!resolved.kit || !resolved.kit.exists) {
        showError('That kit is temporarily unavailable. Please try a different selection.');
        resultEl.hidden = true;
        return;
      }
      if (!resolved.variant || !resolved.variant.available) {
        showError('This kit is currently out of stock.');
        resultEl.hidden = true;
        return;
      }

      clearError();
      var pricing = computePricing(resolved.variant.price, state.packSize);
      currentResolution = { kit: resolved.kit, variant: resolved.variant, pricing: pricing };

      renderResult(kitKey, currentResolution);
      track('kit_recommended', {
        kit: kitKey,
        variantId: resolved.variant.id,
        packSize: state.packSize,
        qty: pricing.qty,
        discountPct: pricing.discountPct
      });
    }

    function renderResult(kitKey, resolution) {
      var kit = resolution.kit, variant = resolution.variant, pricing = resolution.pricing;
      var cleanDesc = decodeEntities(kit.description);

      resultEl.hidden = false;
      resultEl.querySelector('[data-revyn-result-img]').src = variant.featuredImage || kit.image || '';
      resultEl.querySelector('[data-revyn-result-img]').alt = kit.title || '';
      resultEl.querySelector('[data-revyn-result-name]').textContent = kit.title || '';
      resultEl.querySelector('[data-revyn-result-desc]').textContent = cleanDesc;
      resultEl.querySelector('[data-revyn-result-price]').textContent = money(pricing.discountedTotal);

      var compareEl = resultEl.querySelector('[data-revyn-result-compare]');
      var saveEl = resultEl.querySelector('[data-revyn-result-save]');
      if (pricing.discountPct > 0) {
        compareEl.hidden = false;
        compareEl.textContent = money(pricing.lineTotal);
        saveEl.hidden = false;
        saveEl.textContent = 'Save ' + pricing.discountPct + '%';
      } else {
        compareEl.hidden = true;
        saveEl.hidden = true;
      }

      var priceRow = resultEl.querySelector('.revyn-ks__result-price-row');
      flashHighlight(priceRow);

      syncMainProduct(kit, variant, cleanDesc, pricing);
    }

    function syncMainProduct(kit, variant, cleanDesc, pricing) {
      var titleEl = document.querySelector('.evoke-pdp__title');
      if (titleEl) titleEl.textContent = kit.title;

      var descEl = document.querySelector('.evoke-pdp__desc');
      if (descEl && cleanDesc) descEl.textContent = cleanDesc;

      var heroImg = document.querySelector('[data-pgal-main] .evoke-pgal__slide img');
      if (heroImg && (variant.featuredImage || kit.image)) {
        heroImg.src = variant.featuredImage || kit.image;
        heroImg.alt = kit.title;
      }

      var priceEl = document.querySelector('[data-pgal-price]');
      if (priceEl) priceEl.textContent = money(pricing.discountedTotal);

      var compareEl = document.querySelector('[data-pgal-compare]');
      var saveEl = document.querySelector('[data-pgal-save]');
      if (pricing.discountPct > 0) {
        if (compareEl) { compareEl.style.display = ''; compareEl.textContent = money(pricing.lineTotal); }
        if (saveEl) { saveEl.style.display = ''; saveEl.textContent = 'Save ' + pricing.discountPct + '%'; }
      } else {
        if (compareEl) compareEl.style.display = 'none';
        if (saveEl) saveEl.style.display = 'none';
      }

      var priceWrap = document.querySelector('[data-pgal-price-wrap]');
      flashHighlight(priceWrap);

      var form = document.querySelector('#EvokeProductForm');
      var variantIdInput = document.querySelector('[data-pgal-variant-id]');
      if (variantIdInput) variantIdInput.value = variant.id;

      var qtyValEl = document.querySelector('[data-pgal-qty-val]');
      var qtyInputEl = document.querySelector('[data-pgal-qty-input]');
      if (qtyValEl) qtyValEl.textContent = pricing.qty;
      if (qtyInputEl) qtyInputEl.value = pricing.qty;

      var atcLabel = document.querySelector('[data-pgal-atc-label]');
      var atcBtn = document.querySelector('[data-pgal-atc]');
      var buyNowBtn = document.querySelector('[data-pgal-buynow]');
      if (atcLabel) atcLabel.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      if (atcBtn) atcBtn.disabled = !variant.available;
      if (buyNowBtn) buyNowBtn.disabled = !variant.available;

      if (form) {
        var props = lineItemProperties(pricing);
        Object.keys(props).forEach(function (key) {
          var inputName = 'properties[' + key + ']';
          var input = form.querySelector('input[name="' + inputName.replace(/"/g, '\\"') + '"]');
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = inputName;
            form.appendChild(input);
          }
          input.value = props[key];
        });
      }
    }

    function lineItemProperties(pricing) {
      return {
        'Scalp Type': labelFor('scalpType', state.scalpType),
        'Pack Duration': pricing.packLabel,
        'Discount Applied': pricing.discountPct + '%',
        'Quoted Price': money(pricing.discountedTotal)
      };
    }

    function labelFor(key, value) {
      var btn = root.querySelector('[data-revyn-q="' + key + '"] [data-value="' + value + '"]');
      return btn ? btn.textContent.trim() : value;
    }

    var existingForm = document.querySelector('#EvokeProductForm');
    var existingBuyNow = document.querySelector('[data-pgal-buynow]');
    if (existingForm) {
      existingForm.addEventListener('submit', function () {
        if (currentResolution && currentResolution.variant) {
          track('add_to_cart', {
            variantId: currentResolution.variant.id,
            qty: currentResolution.pricing.qty,
            properties: lineItemProperties(currentResolution.pricing)
          });
        }
      });
    }
    if (existingBuyNow) {
      existingBuyNow.addEventListener('click', function () {
        if (currentResolution && currentResolution.variant) {
          track('add_to_cart', {
            variantId: currentResolution.variant.id,
            qty: currentResolution.pricing.qty,
            properties: lineItemProperties(currentResolution.pricing),
            buyNow: true
          });
        }
      });
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-revyn-kit-selector]').forEach(initSelector);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();